import { useCallback, useEffect, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import AssignEmployeToTaskWhenServiceStarts from "../../components/clientServiceSubscriptionTask/updateClientServiceSubscriptionTaskModal/AssignEmployeeToTaskWhenServiceStarts";
import Header from "../../components/Header";
import PageContainer from "../../components/PageContainer";
import DueDateSummarySection, {
    DUE_DATE_REPORT_ITEMS,
    mapDueDateJobRowForAssignments,
    mapDueDateServiceRowForAssignments,
} from "../../components/reports/DueDateSummarySection";
import Sidebar from "../../components/Sidebar";
import {
    getDueTodayTextClass,
    isDueTodayLabel,
} from "../../helpers/dueTodayAppearance";
import { getLoggedInEmployee } from "../../services/getLoggedInEmployee";
import {
    getMyAssignmentsReport,
    getUserJobs,
    getUserServices,
} from "../../services/analytics";
import { term } from "../../terms";
import MyAssignedJobsTable from "./MyAssignedJobsTable";
import MyAssignedServicesTable from "./MyAssignedServicesTable";
import { usePermissions } from "../../context/PermissionsContext";

const SERVICES_ITEM = DUE_DATE_REPORT_ITEMS[0];
const STEPS_ITEM = DUE_DATE_REPORT_ITEMS[1];

const normalizeString = (value) =>
    typeof value === "string" ? value.trim().toLowerCase() : "";

const getEmployeeDisplayName = (value) => {
    if (!value) return "";

    if (typeof value === "string") {
        return value;
    }

    if (typeof value === "object") {
        if (typeof value.fullName === "string" && value.fullName.trim() !== "") {
            return value.fullName;
        }

        const firstName = value.firstName ?? "";
        const lastName = value.lastName ?? "";
        const fullName = `${firstName} ${lastName}`.trim();

        if (fullName !== "") {
            return fullName;
        }
    }

    return "";
};

const getClientServiceSubscriptionId = (row) =>
    row?.clientServiceSubscriptionId ?? row?.id ?? row?.subscriptionId ?? null;

const normalizeServiceAssignmentRow = (row = {}) => {
    const clientServiceSubscriptionId = getClientServiceSubscriptionId(row);
    const assignedBy = getEmployeeDisplayName(
        row.assignedBy ??
            row.assignedByEmployee ??
            row.assignedByUser ??
            row.createdBy,
    );

    return {
        ...row,
        id: clientServiceSubscriptionId ?? row.id ?? null,
        clientServiceSubscriptionId,
        serviceId: row.serviceId ?? null,
        clientId: row.clientId ?? null,
        client: row.client ?? row.clientName ?? "",
        service:
            row.service ??
            row.serviceName ??
            row.clientServiceSubscriptionServiceName ??
            "",
        year: row.year ?? row.clientServiceSubscriptionYear ?? "",
        notes: row.notes ?? "",
        isNew: row.isNew ?? false,
        assignedBy,
    };
};

const normalizeJobAssignmentRow = (row = {}) => {
    const assignedBy = getEmployeeDisplayName(
        row.assignedBy ??
            row.assignedByEmployee ??
            row.assignedByUser ??
            row.createdBy,
    );

    return {
        ...row,
        subscriptionId:
            row.subscriptionId ?? row.clientServiceSubscriptionId ?? row.serviceId,
        clientId: row.clientId ?? null,
        client: row.client ?? row.clientName ?? "",
        service:
            row.service ??
            row.serviceName ??
            row.clientServiceSubscriptionServiceName ??
            "",
        name: row.name ?? "",
        serviceYear:
            row.serviceYear ?? row.clientServiceSubscriptionYear ?? row.year ?? "",
        assignedDate: row.assignedDate ?? row.createdDate ?? row.dateCreated ?? null,
        dueDate: row.dueDate ?? null,
        jobStatus: row.jobStatus ?? row.status ?? 0,
        notes: row.notes ?? "",
        isNew: row.isNew ?? false,
        assignedBy,
    };
};

const getServiceRowKey = (row) => {
    const normalizedRow = normalizeServiceAssignmentRow(row);
    const { clientServiceSubscriptionId, clientId } = normalizedRow;

    if (clientServiceSubscriptionId && clientId) {
        return `${clientServiceSubscriptionId}::${clientId}`;
    }

    return [
        normalizeString(normalizedRow.client),
        normalizeString(normalizedRow.service),
        String(normalizedRow.year ?? ""),
    ].join("::");
};

const getJobRowKey = (row) => {
    const normalizedRow = normalizeJobAssignmentRow(row);
    const { subscriptionId, clientId } = normalizedRow;
    const name = normalizeString(normalizedRow.name);

    if (subscriptionId && clientId && name) {
        return `${subscriptionId}::${clientId}::${name}`;
    }

    return [
        normalizeString(normalizedRow.client),
        normalizeString(normalizedRow.service),
        name,
        String(normalizedRow.serviceYear ?? ""),
    ].join("::");
};

const enrichServiceAssignmentRow = (row, sourceRows) => {
    const normalizedRow = normalizeServiceAssignmentRow(row);
    const sourceMap = new Map(
        (sourceRows || []).map((sourceRow) => {
            const normalizedSourceRow = normalizeServiceAssignmentRow(sourceRow);

            return [getServiceRowKey(normalizedSourceRow), normalizedSourceRow];
        }),
    );
    const matchedRow = sourceMap.get(getServiceRowKey(normalizedRow));

    if (!matchedRow) {
        return normalizedRow;
    }

    return {
        ...matchedRow,
        ...normalizedRow,
        id:
            matchedRow.clientServiceSubscriptionId ??
            normalizedRow.clientServiceSubscriptionId ??
            matchedRow.id ??
            normalizedRow.id,
        clientServiceSubscriptionId:
            matchedRow.clientServiceSubscriptionId ??
            normalizedRow.clientServiceSubscriptionId,
        serviceId:
            matchedRow.serviceId ??
            normalizedRow.serviceId ??
            null,
        notes: matchedRow.notes ?? normalizedRow.notes,
        isNew: matchedRow.isNew ?? normalizedRow.isNew,
        assignedBy: matchedRow.assignedBy ?? normalizedRow.assignedBy,
    };
};

const enrichJobAssignmentRow = (row, sourceRows) => {
    const normalizedRow = normalizeJobAssignmentRow(row);
    const sourceMap = new Map(
        (sourceRows || []).map((sourceRow) => {
            const normalizedSourceRow = normalizeJobAssignmentRow(sourceRow);

            return [getJobRowKey(normalizedSourceRow), normalizedSourceRow];
        }),
    );
    const matchedRow = sourceMap.get(getJobRowKey(normalizedRow));

    if (!matchedRow) {
        return normalizedRow;
    }

    return {
        ...matchedRow,
        ...normalizedRow,
        subscriptionId:
            matchedRow.subscriptionId ??
            normalizedRow.subscriptionId ??
            matchedRow.clientServiceSubscriptionId,
        notes: matchedRow.notes ?? normalizedRow.notes,
        isNew: matchedRow.isNew ?? normalizedRow.isNew,
        assignedBy: matchedRow.assignedBy ?? normalizedRow.assignedBy,
    };
};

const MyAssignmentsReportPage = () => {
    const { can, permissionsLoading } = usePermissions();
    const canReadServices = can("client_services.read");
    const canWriteServices = can("client_services.write");
    const canReadSteps = can("steps.read");
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [report, setReport] = useState({});
    const [pageError, setPageError] = useState(null);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [employeeName, setEmployeeName] = useState("");
    const [loggedInEmployee, setLoggedInEmployee] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState(null);
    const [selectedTableTitle, setSelectedTableTitle] = useState("");
    const [showTable, setShowTable] = useState(false);
    const [servicesData, setServicesData] = useState(null);
    const [jobsData, setJobsData] = useState(null);
    const [tableRows, setTableRows] = useState(null);
    const [dueDateSelectedItem, setDueDateSelectedItem] = useState(SERVICES_ITEM);
    const [dueDateSelectedRows, setDueDateSelectedRows] = useState([]);
    const [isModalStartServiceOpen, setIsModalStartServiceOpen] = useState(false);
    const [clientServiceId, setClientServiceId] = useState(null);
    const [clientId, setClientId] = useState(null);
    const selectedStartServiceRef = useRef({
        clientServiceId: null,
        clientId: null,
    });
    const tableSectionRef = useRef(null);
    const shouldScrollToTableRef = useRef(false);

    useEffect(() => {
        let isMounted = true;

        async function loadPage() {
            if (permissionsLoading) return;

            if (!canReadServices && !canReadSteps) {
                setIsPageLoading(false);
                return;
            }

            setIsPageLoading(true);
            setPageError(null);

            try {
                // The summary endpoint is combined; section rendering below filters
                // its Service/Step totals according to the modular permissions.
                const [assignmentsReportResponse, currentEmployeeResponse] =
                    await Promise.all([
                        getMyAssignmentsReport(),
                        getLoggedInEmployee(),
                    ]);

                if (!isMounted) return;

                if (assignmentsReportResponse.success) {
                    setReport(assignmentsReportResponse.data || {});
                } else {
                    throw new Error(
                        assignmentsReportResponse.message ||
                            "Unable to load the assignments report.",
                    );
                }

                if (currentEmployeeResponse.success) {
                    setLoggedInEmployee(currentEmployeeResponse.data.employeeResponse);
                } else {
                    throw new Error(
                        currentEmployeeResponse.message ||
                            "Unable to load the current employee.",
                    );
                }
            } catch (error) {
                if (!isMounted) return;
                setPageError(error);
                toast.error("Unable to load assignments report.");
            } finally {
                if (isMounted) {
                    setIsPageLoading(false);
                }
            }
        }

        loadPage();

        return () => {
            isMounted = false;
        };
    }, [canReadServices, canReadSteps, permissionsLoading]);

    useEffect(() => {
        if (!canReadServices && canReadSteps) {
            setDueDateSelectedItem(STEPS_ITEM);
        } else if (canReadServices) {
            setDueDateSelectedItem(SERVICES_ITEM);
        }
    }, [canReadServices, canReadSteps]);

    const scrollToTable = useCallback(() => {
        if (!tableSectionRef.current) return;

        requestAnimationFrame(() => {
            tableSectionRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        });
    }, []);

    useEffect(() => {
        if (!shouldScrollToTableRef.current) return;
        if (!showTable || !selectedFilter || tableRows === null) return;

        shouldScrollToTableRef.current = false;
        scrollToTable();
    }, [scrollToTable, selectedFilter, showTable, tableRows]);

    const fetchAssignedServices = async () => {
        if (!canReadServices) return [];
        const response = await getUserServices();

        if (!response.success) {
            toast.error("Error fetching services.");
            return [];
        }

        const rows = (response.data.servicesAssignedToUser || []).map(
            normalizeServiceAssignmentRow,
        );
        setServicesData(rows);
        setEmployeeName(response.data.fullName || "");

        return rows;
    };

    const loadServicesTable = async (useAllRows = true) => {
        const rows = await fetchAssignedServices();

        const nextRows = useAllRows ? rows : rows.filter((service) => service.isNew);
        setTableRows(nextRows);
        setSelectedFilter("services");

        return rows;
    };

    const fetchAssignedJobs = async () => {
        if (!canReadSteps) return [];
        const response = await getUserJobs();

        if (!response.success) {
            toast.error("Error fetching steps.");
            return [];
        }

        const rows = (response.data.tasksAssignedToUser || []).map(
            normalizeJobAssignmentRow,
        );
        setJobsData(rows);
        setEmployeeName((currentName) => currentName || response.data.fullName || "");

        return rows;
    };

    const loadJobsTable = async (useAllRows = true) => {
        const rows = await fetchAssignedJobs();

        const nextRows = useAllRows ? rows : rows.filter((job) => job.isNew);
        setTableRows(nextRows);
        setSelectedFilter("jobs");

        return rows;
    };

    const handleSummarySelection = async (filterName, mode) => {
        const isServices = filterName === "services";
        if (
            (isServices && !canReadServices) ||
            (!isServices && !canReadSteps)
        )
            return;
        const isNewOnly = mode === "new";

        shouldScrollToTableRef.current = true;
        setShowTable(true);
        setDueDateSelectedItem(isServices ? SERVICES_ITEM : STEPS_ITEM);
        setDueDateSelectedRows([]);
        setSelectedFilter(filterName);
        setSelectedTableTitle(
            `${isNewOnly ? "New" : "Total"} ${isServices ? "Services" : term("step_plural")}`,
        );

        if (isServices) {
            const rows =
                servicesData?.map(normalizeServiceAssignmentRow) ||
                (await loadServicesTable(true)).map(normalizeServiceAssignmentRow);
            setTableRows(isNewOnly ? rows.filter((service) => service.isNew) : rows);
            return;
        }

        const rows =
            jobsData?.map(normalizeJobAssignmentRow) ||
            (await loadJobsTable(true)).map(normalizeJobAssignmentRow);
        setTableRows(isNewOnly ? rows.filter((job) => job.isNew) : rows);
    };

    const handleDueDateSelection = useCallback(
        async ({ rows, reportName, selectedItem }) => {
            const isServices = selectedItem.label === "Services";
            if (
                (isServices && !canReadServices) ||
                (!isServices && !canReadSteps)
            )
                return;
            const sourceRows = isServices
                ? servicesData || (await fetchAssignedServices())
                : jobsData || (await fetchAssignedJobs());
            const mappedRows = isServices
                ? rows.map(mapDueDateServiceRowForAssignments)
                : rows.map(mapDueDateJobRowForAssignments);
            const enrichedRows = isServices
                ? mappedRows.map((row) => enrichServiceAssignmentRow(row, sourceRows))
                : mappedRows.map((row) => enrichJobAssignmentRow(row, sourceRows));

            shouldScrollToTableRef.current = true;
            setShowTable(true);
            setDueDateSelectedItem(selectedItem);
            setDueDateSelectedRows(enrichedRows);
            setSelectedFilter(isServices ? "services" : "jobs");
            setSelectedTableTitle(
                `${reportName} ${isServices ? "Services" : term("step_plural")}`,
            );
            setTableRows(enrichedRows);
        },
        [jobsData, servicesData, canReadServices, canReadSteps],
    );

    const onHandleStartService = (nextClientServiceId, nextClientId) => {
        selectedStartServiceRef.current = {
            clientServiceId: nextClientServiceId,
            clientId: nextClientId,
        };
        setClientId(nextClientId);
        setClientServiceId(nextClientServiceId);
        setIsModalStartServiceOpen(true);
    };

    const handleStartServiceSuccess = useCallback(async () => {
        const {
            clientServiceId: selectedClientServiceId,
            clientId: selectedClientId,
        } = selectedStartServiceRef.current;

        await loadServicesTable(true);
        setIsModalStartServiceOpen(false);

        if (
            !canReadServices ||
            !selectedClientServiceId ||
            !selectedClientId
        ) {
            return;
        }

        navigate(
            `/clients/service/${selectedClientServiceId}/client/${selectedClientId}`,
        );
    }, [navigate, canReadServices]);

    const renderMetricButton = (label, value, onClick, accentClass) => (
        <button
            type="button"
            onClick={onClick}
            className="flex flex-col items-center w-full text-center transition-transform duration-150 hover:scale-[1.01]"
        >
            <h2 className="text-xl font-semibold text-gray-800">
                {label}
            </h2>
            <p
                className={`mt-6 text-4xl font-bold transition-transform duration-500 ease-in-out transform-gpu ${accentClass}`}
            >
                {value || 0}
            </p>
        </button>
    );

    const rowsToRender = tableRows || dueDateSelectedRows;

    return (
        <section>
            <div className="flex h-screen overflow-hidden">
                {canWriteServices && loggedInEmployee && clientId && (
                    <AssignEmployeToTaskWhenServiceStarts
                        open={isModalStartServiceOpen}
                        setOpen={setIsModalStartServiceOpen}
                        loggedInEmployee={loggedInEmployee}
                        clientSubscriptionId={clientServiceId}
                        newClientService={handleStartServiceSuccess}
                        clientId={clientId}
                    />
                )}
                <Toaster />

                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <main>
                        <PageContainer
                            isLoading={permissionsLoading || isPageLoading}
                            error={pageError}
                            loadingText="Loading report data..."
                            showOverlay={false}
                        >
                            <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                                <div className="flex flex-col items-start max-w-2xl">
                                    <h1 className="p-4 text-2xl font-bold md:text-3xl text-slate-800">
                                        My Assignments Report
                                    </h1>
                                </div>
                                <div className="grid grid-cols-1 gap-6 mt-6 sm:grid-cols-2">
                                    {canReadServices && (
                                    <div className="flex flex-col p-6 bg-teal-100 rounded-lg shadow-lg">
                                        {renderMetricButton(
                                            "TOTAL SERVICES",
                                            report.totalServices,
                                            () =>
                                                handleSummarySelection(
                                                    "services",
                                                    "total",
                                                ),
                                            "text-slate-500",
                                        )}

                                        <div className="w-full h-px my-6 bg-teal-200" />

                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleSummarySelection(
                                                    "services",
                                                    "new",
                                                )
                                            }
                                            className="flex flex-col items-center w-full text-center transition-transform duration-150 hover:scale-[1.01]"
                                        >
                                            <h2 className="text-xl font-semibold text-gray-800">
                                                NEW SERVICES
                                            </h2>
                                            <p className="mt-1 text-sm font-medium text-slate-500">
                                                (last 7 days)
                                            </p>
                                            <p className="mt-6 text-4xl font-bold text-green-500 transition-transform duration-500 ease-in-out transform-gpu">
                                                {report.newServices || 0}
                                            </p>
                                        </button>
                                    </div>
                                    )}

                                    {canReadSteps && (
                                    <div className="flex flex-col p-6 bg-teal-100 rounded-lg shadow-lg">
                                        {renderMetricButton(
                                            `TOTAL ${term("step_plural").toUpperCase()}`,
                                            report.totalJobs,
                                            () =>
                                                handleSummarySelection("jobs", "total"),
                                            "text-slate-500",
                                        )}

                                        <div className="w-full h-px my-6 bg-teal-200" />

                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleSummarySelection("jobs", "new")
                                            }
                                            className="flex flex-col items-center w-full text-center transition-transform duration-150 hover:scale-[1.01]"
                                        >
                                            <h2 className="text-xl font-semibold text-gray-800">
                                                NEW {term("step_plural").toUpperCase()}
                                            </h2>
                                            <p className="mt-1 text-sm font-medium text-slate-500">
                                                (last 7 days)
                                            </p>
                                            <p className="mt-6 text-4xl font-bold text-green-500 transition-transform duration-500 ease-in-out transform-gpu">
                                                {report.newJobs || 0}
                                            </p>
                                        </button>
                                    </div>
                                    )}
                                </div>

                                {(canReadServices || canReadSteps) && (
                                <DueDateSummarySection
                                    employeeId={loggedInEmployee?.employeeId}
                                    selectedItem={dueDateSelectedItem}
                                    onSelectedItemChange={setDueDateSelectedItem}
                                    onSelectionChange={handleDueDateSelection}
                                    showEmployeeSelector={false}
                                    showDepartmentSelector={false}
                                    onError={(error) => {
                                        if (error) {
                                            toast.error(
                                                "Unable to load due date report.",
                                            );
                                        }
                                    }}
                                    reportItems={[
                                        ...(canReadServices
                                            ? [SERVICES_ITEM]
                                            : []),
                                        ...(canReadSteps ? [STEPS_ITEM] : []),
                                    ]}
                                />
                                )}

                                {showTable && selectedFilter && (
                                    <div ref={tableSectionRef} className="mt-10">
                                        <h2
                                            className={`pb-8 text-2xl font-bold ${
                                                isDueTodayLabel(
                                                    selectedTableTitle,
                                                )
                                                    ? getDueTodayTextClass()
                                                    : "text-black"
                                            }`}
                                        >
                                            {selectedTableTitle}
                                        </h2>

                                        {selectedFilter === "services" ? (
                                            <MyAssignedServicesTable
                                                data={rowsToRender}
                                                employeeName={employeeName}
                                                onHandleUpdateServiceStatus={
                                                    onHandleStartService
                                                }
                                                canWriteServices={
                                                    canWriteServices
                                                }
                                                canReadServices={
                                                    canReadServices
                                                }
                                            />
                                        ) : (
                                            <MyAssignedJobsTable
                                                data={rowsToRender}
                                                employeeName={employeeName}
                                                canNavigateToService={
                                                    canReadServices
                                                }
                                                canReadSteps={canReadSteps}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </PageContainer>
                    </main>
                </div>
            </div>
        </section>
    );
};

export default MyAssignmentsReportPage;
